import { useCallback, useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type {
  CellClickedEvent,
  ColDef,
  ICellRendererParams
} from "ag-grid-community";
import { Home01Icon, Unlink02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { BaseInput, InputSize } from "shared/components";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { AgGrid } from "shared/components/AgGrid";
import { Pagination } from "shared/components/Pagination";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import addressApi from "api/landlord/address";
import {
  FRAUD_ALERTS_KEY,
  MONITORED_PROPERTIES_KEY,
  propertyShieldApi,
  PROPERTY_SHIELD_INTEGRATION_KEY,
  type MonitoredProperty,
  type MonitoredPropertiesResponse
} from "api/propertyShield";
import { usePropertyShieldIntegration } from "shared/queries/usePropertyShieldIntegration";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationDetailHeader } from "views/settings-integration/integrations/shared/IntegrationDetailHeader";
import { getErrorMessage } from "utils/error";

const PER_PAGE = 20;
const TOGGLE_MONITORING_KEY = [MONITORED_PROPERTIES_KEY, "TOGGLE"];

export const PropertyShieldView = () => {
  const { type, refreshPartnerData } = useAuthContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { Property, properties: propertiesLabel } =
    useUnitsPropertiesDictionary();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [page, setPage] = useState(1);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const { data: integration } = usePropertyShieldIntegration();

  const params = {
    search: debouncedSearch || undefined,
    page,
    per_page: PER_PAGE
  };
  const { data, isFetching } = useQuery({
    queryKey: [MONITORED_PROPERTIES_KEY, params],
    queryFn: () => propertyShieldApi.getMonitoredProperties(params),
    enabled: type.isListingFraudAlerts,
    placeholderData: keepPreviousData,
    meta: { onError: (error: unknown) => toast.error(getErrorMessage(error)) }
  });

  const rows = data?.items ?? [];
  const pageCount = Math.ceil((data?.total ?? 0) / PER_PAGE);

  // A mutation tracks only its latest variables, so pending state is per row
  // and the switch value is written to the cache optimistically.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );

  const setRowMonitoring = (id: number, enabled: boolean) =>
    queryClient.setQueriesData<MonitoredPropertiesResponse>(
      { queryKey: [MONITORED_PROPERTIES_KEY] },
      (prev) =>
        prev && {
          ...prev,
          items: prev.items.map((item) =>
            item.id === id ? { ...item, monitoring_enabled: enabled } : item
          )
        }
    );

  const { mutate: toggleMonitoring } = useMutation({
    mutationKey: TOGGLE_MONITORING_KEY,
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      addressApi.editProperty({
        propertyId: id,
        property_shield_monitoring_enabled: enabled
      }),
    onMutate: async ({ id, enabled }) => {
      setPendingIds((prev) => new Set(prev).add(id));
      await queryClient.cancelQueries({ queryKey: [MONITORED_PROPERTIES_KEY] });
      setRowMonitoring(id, enabled);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [FRAUD_ALERTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["properties", String(id)] });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_SHIELD_INTEGRATION_KEY]
      });
    },
    onError: (error, { id, enabled }) => {
      setRowMonitoring(id, !enabled);
      toast.error(
        `Failed to update listing fraud monitoring: ${getErrorMessage(error)}`
      );
    },
    onSettled: (_, __, { id }) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      // Refetch only after the last toggle settles, so it can't overwrite an
      // in-flight row.
      if (queryClient.isMutating({ mutationKey: TOGGLE_MONITORING_KEY }) === 1)
        queryClient.invalidateQueries({ queryKey: [MONITORED_PROPERTIES_KEY] });
    }
  });

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationKey: [PROPERTY_SHIELD_INTEGRATION_KEY, "DELETE"],
    mutationFn: propertyShieldApi.disconnect,
    onSuccess: async (next) => {
      queryClient.setQueryData([PROPERTY_SHIELD_INTEGRATION_KEY], next);
      queryClient.invalidateQueries({ queryKey: [MONITORED_PROPERTIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [FRAUD_ALERTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      // Disconnecting hides Listing Fraud Alerts across the portal.
      await refreshPartnerData();
      toast.success("Property Shield integration disconnected successfully");
      navigate("/settings/integration");
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const columns = useMemo<ColDef<MonitoredProperty>[]>(
    () => [
      {
        headerName: Property,
        colId: "property",
        flex: 1,
        minWidth: 240,
        cellRenderer: (cell: ICellRendererParams<MonitoredProperty>) =>
          cell.data ? (
            <div className="flex flex-col justify-center gap-1">
              <p className="truncate text-sm font-medium text-content-1000">
                {cell.data.name || cell.data.address || `#${cell.data.id}`}
              </p>
              {cell.data.name && cell.data.address ? (
                <p className="truncate text-[13px] leading-4 text-content-400">
                  {cell.data.address}
                </p>
              ) : null}
            </div>
          ) : null
      },
      {
        headerName: "Enabled",
        colId: "enabled",
        width: 120,
        cellRenderer: (cell: ICellRendererParams<MonitoredProperty>) => {
          const row = cell.data;
          if (!row) return null;
          return (
            <Switch
              size={SwitchSize.SM}
              checked={row.monitoring_enabled}
              disabled={pendingIds.has(row.id)}
              onChange={(enabled) => toggleMonitoring({ id: row.id, enabled })}
            />
          );
        }
      }
    ],
    [Property, pendingIds, toggleMonitoring]
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent<MonitoredProperty>) => {
      const row = event.data;
      if (!row || event.column.getColId() === "enabled") return;

      navigate(`/properties/${row.id}`);
    },
    [navigate]
  );

  return (
    <Layout
      header={
        <IntegrationDetailHeader
          id={IntegrationId.PROPERTY_SHIELD}
          name="Property Shield"
          path="/settings/integration/property-shield"
          integrationType="Property shield"
          onDisconnect={
            integration?.connected
              ? () => setConfirmDisconnect(true)
              : undefined
          }
          isDisconnecting={isDisconnecting}
        />
      }
    >
      <div className="space-y-4">
        <BaseInput
          id="property-shield-search"
          autoComplete="off"
          placeholder="Search"
          value={search}
          size={InputSize.SM}
          className="!mb-0 max-w-[361px] w-full bg-white"
          onChange={(value: string) => {
            setSearch(value);
            setPage(1);
          }}
        />

        <AgGrid<MonitoredProperty>
          columns={columns}
          rows={rows}
          loading={isFetching && !rows.length}
          rowHeight={64}
          themeOverrides={{ "--ag-cell-horizontal-padding": "16px" }}
          onCellClicked={onCellClicked}
          noRowsFallback={
            <NoRowsFallback
              Icon={Home01Icon}
              title={`No ${propertiesLabel} found`}
              text={
                debouncedSearch
                  ? "We couldn't find any data matching your query"
                  : `Your ${propertiesLabel} will appear here`
              }
            />
          }
          footer={
            <Pagination
              className="justify-center md:justify-end"
              total={pageCount}
              current={page}
              onChange={setPage}
            />
          }
          showFooter={pageCount > 1}
        />
      </div>

      <ConfirmDialog
        opened={confirmDisconnect}
        onClose={() => {
          if (!isDisconnecting) setConfirmDisconnect(false);
        }}
        onConfirm={() => disconnect()}
        title="Disconnect Property Shield?"
        description={`Fraud monitoring will be turned off for all your ${propertiesLabel}. You can reconnect at any time.`}
        icon={{ icon: Unlink02Icon }}
        confirmButton={{ label: "Disconnect", loading: isDisconnecting }}
      />
    </Layout>
  );
};
